import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { DrawerLink } from '../../../app/DrawerLink';
import { Explicit } from '../../../components/ui/Explicit';
import { FavoriteButton } from '../../../components/ui/FavoriteButton';
import { Verified } from '../../../components/ui/Verified';
import { api } from '../../../lib/api';
import { useSession } from '../../../stores/session';
import { fromSeed, normalise, type Character as CharacterConfig } from '../../character/config';
import { AddToPlaylist } from '../../library/AddToPlaylist';
import { useTrackFavorite } from '../../tracks/useTrackFavorite';
import { useHype } from '../hype';
import { HypeMeter } from '../HypeMeter';
import { usePeaks } from '../../player/peaks';
import { StreamPlayer } from '../StreamPlayer';
import { useHostingHere } from '../hosting';
import { TwitchPlayer, twitchChannel } from '../TwitchStage';
import { duration, type Room } from '../api';
import { stopListening } from '../listening';
import { REACTIONS, useRoomReactions } from '../reactions';
import { positionNow } from '../sync';
import { LiveStage, STAGE_CAPACITY, STAGE_FRONT_AT } from './LiveStage';
import { StageScene } from './StageScene';

/** The largest 16:9 box that fits the section, in container units. */
const BOX = {
  width: 'min(100cqw, calc(100cqh * 16 / 9))',
  height: 'min(100cqh, calc(100cqw * 9 / 16))',
} as const;

/**
 * The live room, as a venue.
 *
 * ## What this replaces, and what it does not
 *
 * `NowPlaying` puts a square of artwork beside a column of text. This puts the
 * same room on a stage: the host behind the decks as their own character, the
 * people actually listening standing in front of them, and whatever the room is
 * putting out on the screen behind.
 *
 * The queue strip below is untouched. Everything that used to sit *under* the
 * deck is gone: the room's name was printed a second time on the room's own
 * page, and listening and submitting moved into the status line - which is
 * itself drawn over the top of the venue now rather than above it, because a
 * row of chrome in the layout is a row of height the stage does not get.
 *
 * Both overlays are scrims rather than panels. The scene is dark sky at the top
 * and dark floor at the bottom, so a box drawn on either would read as a box;
 * a gradient just makes the type legible over whatever is behind it.
 *
 * ## The stage takes the whole section, and is fitted rather than cropped
 *
 * The section is about 2.6:1 and the scene is 16:9, so something has to give.
 * Cropping to fill was the first answer and it is the wrong one: the visible
 * band at that shape is 747 of the scene's 1080 units, and keeping the screen
 * *and* the crowd needs 950 - so filling the width cuts the audience off at the
 * shins, which is the one part of this worth having.
 *
 * So it fits by height, and the parts of the scene that have no perspective in
 * them are drawn wider than it: the backdrop is stretched across the whole
 * container and the painted crowd is tiled across it, so what is beside the
 * stage is more room rather than a letterbox bar.
 *
 * The painted crowd does run the full width, though. It is rows of blobs on a
 * dark ground with no perspective and no fixed place on the stage, so it is the
 * one part of the scene that can be repeated sideways without looking repeated
 * - three copies at exactly the stage's own size, which is what makes the floor
 * line continuous across the join.
 *
 * The box is sized in container units rather than by telling the SVG to
 * `slice`, because the screen is placed as a percentage of that box: scaling
 * the box keeps the arithmetic exact, and `preserveAspectRatio` would not.
 *
 * What is playing is drawn over the bottom of it, where the artwork is dark
 * floor and there is nothing to cover - the same place a video player puts the
 * same information, for the same reason.
 *
 * ## One screen, three sources
 *
 * The screen is a 16:9 hole in the artwork and everything drops into it
 * unchanged: `TwitchPlayer` for a simulcast, `StreamPlayer` for a studio or
 * encoder broadcast, and the track's artwork when the room is playing its own
 * catalogue. That is the whole reason the screen is HTML over the SVG rather
 * than inside it - see `LiveStage`.
 *
 * ## The crowd is the crowd
 *
 * Read from the same `['crowd', username]` query the strip already uses, so
 * this costs no request: the people on the stage are the people in the room,
 * with the characters they built. Somebody who has never opened the maker gets
 * the one seeded from their name, exactly as they appear everywhere else.
 */

interface Face {
  username: string;
  display_name: string;
  character: CharacterConfig | null;
  since: string;
}

export function StageDeck({
  room,
  listening,
  onListen,
  theme = 'default',
  header,
}: {
  room: Room;
  listening: boolean;
  onListen: () => void;
  /** The season, from the room's payload. See `config/stage-themes.php`. */
  theme?: string;
  /**
   * The room's name and status line, drawn over the top of the venue.
   *
   * Passed in rather than built here: it is the same `Status` the old deck
   * uses, with the same clock and the same tip button, and a second copy would
   * be a second answer to how long a broadcast has been running.
   */
  header?: ReactNode;
}) {
  const me = useSession((state) => state.user);
  const [position, setPosition] = useState(0);

  /*
   * Whether the screen has been asked to fill the frame.
   *
   * Owned here rather than by `LiveStage`, because the button has to sit with
   * the other things somebody presses - a control inside the stage lost every
   * fight with this row, and no `z-index` inside a stacking context can escape
   * it.
   *
   * The venue's own chrome steps out of the way when it is on: the room's name
   * and what is playing are both drawn over the artwork, and neither has any
   * business sitting on a broadcast somebody has just made full size.
   */
  const [focused, setFocused] = useState(() => remembered());

  useEffect(() => {
    try {
      window.localStorage.setItem(REMEMBER, focused ? '1' : '0');
    } catch {
      // See `remembered`. Losing the preference is the whole cost.
    }
  }, [focused]);

  const twitch = twitchChannel(room);

  /*
   * Whether this tab is the one sending the broadcast.
   *
   * A host on their own profile mid-broadcast must not be handed their own
   * picture three seconds late with the audio doubled against the deck they are
   * mixing from - the same rule `NowPlaying` follows, for the same reason.
   */
  const producing = useHostingHere(room.username);

  const { data } = useQuery({
    queryKey: ['crowd', room.username],
    queryFn: () =>
      api.get<{ faces: Face[]; total: number; hidden: number }>(
        `/users/${encodeURIComponent(room.username)}/live/crowd`,
      ),
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });

  /* Same clock `NowPlaying` runs: extrapolated locally, snapped by every poll. */
  useEffect(() => {
    const update = () => {
      const at = positionNow(room) ?? 0;
      const length = room.now_playing?.duration_ms;

      setPosition(length == null ? at : Math.min(at, length));
    };

    update();
    const timer = setInterval(update, 400);

    return () => clearInterval(timer);
  }, [room]);

  const track = room.holding ? null : room.now_playing;

  useTrackFavorite(track?.id);

  /*
   * The record itself, for the bars on the screen.
   *
   * The same file the scrubber reads, so a listener has usually already paid
   * for it - and null whenever there is no track, no waveform or a stream on
   * the screen, which is what puts the bars back on their CSS loop. See `eq.ts`
   * for why this is the room's clock rather than an audio element's.
   */
  const peaks = usePeaks(track?.waveform_url ?? null);

  /*
   * The host, as themselves.
   *
   * `normalise` rather than the raw row, because a character saved before the
   * last drop is missing fields the drawing reads - and `fromSeed` for a host
   * who has never opened the maker, which is the same face they wear in every
   * other list on this site.
   */
  const host: CharacterConfig = room.host.character
    ? normalise(room.host.character)
    : fromSeed(room.username);

  /*
   * Who is standing there.
   *
   * Capped at what the scene has places for - asking the endpoint is free but
   * drawing a hundred people into eleven spots is not, and the ones past the
   * edge would be drawn on top of each other.
   *
   * The viewer is pulled to the front of the queue, so if they are in the room
   * they are on the stage rather than eleventh in a list and cut. Their chip is
   * the only name drawn, because it is the only one somebody is looking for.
   */
  /*
   * What the room is doing, pushed over the room's own channel.
   *
   * The stage assigned each standing position an animation for a while, which
   * meant somebody was dancing because of where they happened to be stood -
   * and one of them clapped for the entire broadcast. Everybody idles now, and
   * a reaction is a thing that *happens*, to the person who sent it, for as
   * long as the server says.
   */
  const { reacting, react } = useRoomReactions(room.id, room.username, me?.username);

  /*
   * How hard the room is going, and the viewer's own way of pushing it.
   *
   * Only tappable while actually listening: the server refuses one from outside
   * the room, and `listening` is the same condition the reaction buttons use.
   */
  const hype = useHype(room.id, room.username, listening);

  const faces = data?.faces ?? [];
  const mine = faces.filter((face) => face.username === me?.username);
  const others = faces.filter((face) => face.username !== me?.username);

  /*
   * The viewer first, which the scene draws centre-front. Everybody else fills
   * outwards from there and then into the row behind - see `CROWD_FRONT`.
   */
  const ordered = [
    ...others.slice(0, STAGE_FRONT_AT),
    ...mine,
    ...others.slice(STAGE_FRONT_AT),
  ];

  const crowd = ordered.slice(0, STAGE_CAPACITY).map((face) => ({
    config: face.character ? normalise(face.character) : fromSeed(face.username),
    // Standing there, until the room says otherwise.
    anim: reacting[face.username] ?? 'idle',
    // One name on the stage, and it is yours. Eleven chips would be a list.
    ...(face.username === me?.username ? { name: face.username } : {}),
  }));

  /*
   * A room that is going, which is now two different things being asked at once.
   *
   * It used to be ten listeners, which is a proxy: a full room is *probably* a
   * lively one. The meter is the thing itself - people actually reacting, right
   * now - so the venue speeds up when either says so, and the proxy stays for
   * the big quiet room where everybody is listening rather than pressing.
   */
  const busy = (room.listener_count ?? 0) >= 10 || hype.busy;

  /*
   * Anything with a picture in it can fill the frame.
   *
   * A stream obviously. Artwork was left out at first on the argument that a
   * still blown up is a worse picture than the room around it - which is a
   * matter of taste, and not one worth taking off somebody who wants to look at
   * a cover full size.
   *
   * What is still refused is the card with the track's *name* on it, drawn when
   * there is no artwork: filling a screen with two lines of text is a control
   * that does nothing anybody wanted. Which also means the zoom cannot strand
   * you - it disappears with the picture, and a remembered zoom only applies to
   * a room that has one.
   */
  const artwork = track !== null && (track.artwork ?? track.artwork_small) !== null;
  const zoomable = Boolean(twitch) || (room.stream_url !== null && !producing) || artwork;
  const zoomed = focused && zoomable;

  return (
    /*
      `container-type: size` is what makes the `cq` units below mean this box.
      Without it they resolve against the nearest ancestor that has one, or the
      viewport - and the stage would be sized to the wrong thing.
    */
    <div
      className="relative min-h-0 flex-1 overflow-hidden rounded-[var(--radius-panel)] border border-frame"
      // The backdrop is the scene's own, stretched across this box by
      // `LiveStage` - a CSS gradient here was a second one on a different
      // vertical scale, and the join between them was the hard line down each
      // side that this replaces.
      style={{ containerType: 'size', background: '#0B0B0B' }}
      data-stage-fill
    >
      {/*
        Contain, done exactly.

        `min(100cqw, 100cqh*16/9)` by `min(100cqh, 100cqw*9/16)` is the largest
        16:9 box that fits the container, centred. `max` instead of `min` is
        cover, and cover is what cut the crowd off - see the note at the top.
      */}
      {/*
        The room the stage stands in, edge to edge and behind everything.

        Two gradients stretched across this whole box rather than fitted into
        the 16:9 one, which is the point: the backdrop used to stop dead where
        the stage did, with a CSS gradient here taking over on a different
        vertical scale, and the join between the two was the hard vertical line
        down each side.

        Here rather than inside `LiveStage`, because `LiveStage` *is* the 16:9
        box - anything absolute in there is measured against the stage, which is
        the thing this needs to be wider than.
      */}
      <div className="absolute inset-0" data-stage-sky>
        <StageScene layer="sky" host={host} crowd={[]} theme={theme} />
      </div>

      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={BOX}
      >
        <LiveStage
          host={host}
          crowd={crowd}
          theme={theme}
          hype={
            listening
              ? { total: hype.total, milestone: hype.milestone, onTap: hype.tap }
              : null
          }
          /*
           * Whether the screen is a picture rather than a player, which decides
           * whether a tap over it belongs to the room or to the thing playing.
           * Artwork, the title card and the host's own "you are broadcasting
           * this" note are all pictures; a stream and a simulcast are not.
           */
          picture={!twitch && (producing || room.stream_url === null)}
          busy={busy}
          eq={
            track && track.duration_ms
              ? {
                  peaks,
                  duration: track.duration_ms,
                  /*
                   * Read once a frame off the room's own clock rather than off
                   * `position` above, which is state on a 400ms timer - the
                   * bars would step five times a second instead of moving.
                   */
                  at: () => positionNow(room) ?? 0,
                  playing: !room.paused,
                }
              : null
          }
          // Only worth filling the frame with something moving. A still cover
          // blown up is a worse picture than the room around it.
          focused={zoomed}
        >
          {twitch ? (
            <TwitchPlayer channel={twitch} room={room} />
          ) : producing ? (
            <div className="flex h-full w-full items-center justify-center px-8 text-center">
              <p className="text-sm text-white/70">
                You are broadcasting this from the studio. Your listeners see it here.
              </p>
            </div>
          ) : room.stream_url ? (
            <StreamPlayer
              url={room.stream_url}
              hls={room.stream_hls}
              listening={listening}
              onJoin={onListen}
              onLeave={() => stopListening()}
            />
          ) : track && (track.artwork ?? track.artwork_small) ? (
            /*
              A square cover on a 16:9 screen, whole.
            
              It used to be `object-cover`, which fills the screen by throwing
              away the left and right thirds of the artwork - on a wall-sized
              screen whose entire job is to show what is playing. Contained
              instead, and the space either side is the same picture blown up
              and blurred behind it.
            
              Which is the trick every music player uses for this, and it works
              for a reason: the colour beside the cover is the cover's own, so
              the screen takes on the record rather than framing it in black.
            */
            <div className="relative h-full w-full overflow-hidden" data-stage-artwork>
              {/*
                Scaled past the edges before it is blurred. A blur samples
                outside the element and finds nothing there, so an unscaled copy
                fades to transparent at the frame and draws a soft dark border
                around itself.
              */}
              <img
                src={track.artwork ?? track.artwork_small ?? undefined}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full scale-125 object-cover blur-2xl"
              />

              {/* Knocked back, so a bright cover does not glare either side of
                  itself and the sharp copy stays the thing being looked at. */}
              <div className="absolute inset-0 bg-black/30" aria-hidden />

              {/*
                Sat on the screen rather than filling it.

                `88%` of the height rather than a padding, because percentage
                padding resolves against the *width* on every side - on a 16:9
                box that turns "a little off the top" into almost twice as much
                as asked for. Sizing the cover instead keeps the gap even and
                keeps it proportional at both sizes this is drawn at, which are
                a long way apart once the screen fills the frame.

                The shadow is what stops it looking painted onto the backdrop:
                the blurred copy behind is the same picture, so without one the
                sharp edge reads as a crop of the blur rather than as a record
                standing in front of it.
              */}
              <div className="relative flex h-full w-full items-center justify-center">
                <img
                  src={track.artwork ?? track.artwork_small ?? undefined}
                  alt=""
                  data-stage-cover
                  className="h-[88%] w-auto max-w-[88%] rounded-xl object-contain shadow-[0_18px_60px_rgb(0_0_0/0.65)] ring-1 ring-white/10"
                />
              </div>
            </div>
          ) : track ? (
            /*
              Playing, with no cover on it.

              Not the between-tracks card, which is what this fell through to
              and which is a lie while a record is on. A venue screen with no
              artwork to show puts up the name of what is playing, so this does
              - and it is the only place the title appears at a size anybody
              across the room could read.
            */
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-10 text-center">
              <span className="line-clamp-2 text-3xl font-bold leading-tight tracking-[-0.02em] text-white/90">
                {track.title}
              </span>
              <span className="truncate text-base text-white/45">{track.artist.display_name}</span>
            </div>
          ) : (
            /*
              Between tracks, which is a real moment in a broadcast rather than
              a missing one - and on a screen the size of a wall, saying so
              plainly beats a grey rectangle.
            */
            <div className="flex h-full w-full flex-col items-center justify-center gap-1">
              <span className="meta-caps text-white/50">Between tracks</span>
              <span className="text-sm text-white/35">still on air</span>
            </div>
          )}
        </LiveStage>
      </div>

      {/* ── The room's name and status, over the sky ──────────────────── */}
      {header !== undefined && !focused && (
        <div
          data-stage-header
          className="pointer-events-none absolute inset-x-0 top-0 z-10 px-6 pb-14 pt-5"
          style={{
            backgroundImage: 'linear-gradient(to bottom, rgb(8 6 16 / 0.85), transparent)',
          }}
        >
          {/*
            The scrim ignores the pointer so a click lands on the stage under
            it; the row itself takes it back, because half of it is buttons.
          */}
          <div className="pointer-events-auto">{header}</div>
        </div>
      )}

      {/* ── What is playing, over the floor ───────────────────────────── */}
      {track && !focused && (
        <div
          data-stage-nowplaying
          className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col gap-3 px-6 pb-3 pt-20"
          style={{
            // A scrim rather than a panel: the bottom of the scene is dark
            // floor and a crowd, and a box drawn on them would read as a box.
            backgroundImage: 'linear-gradient(to top, rgb(8 6 16 / 0.94), rgb(8 6 16 / 0.7) 45%, transparent)',
          }}
        >
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
            <div className="pointer-events-auto flex min-w-0 flex-col gap-0.5">
              <span className="meta-caps text-white/45">Now playing</span>

              <h2 className="truncate text-xl font-bold leading-tight tracking-[-0.02em] text-white">
                <DrawerLink to={`/tracks/${track.id}`} className="hover:underline">
                  {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
                  {track.title}
                </DrawerLink>
              </h2>

              <Link
                to={`/${track.artist.username}`}
                className="truncate text-[14px] text-white/65 hover:text-white hover:underline"
              >
                {track.artist.display_name}
                {track.artist.verified && <Verified />}
              </Link>
            </div>

            <div className="flex shrink-0 items-center gap-4">
              {/*
                Reacting, next to the track it is a reaction to.

                Only for somebody actually listening: the server refuses one
                from outside the room, and a button that exists to be refused is
                worse than one that is not there. It went missing when the crowd
                strip did - the strip carried these, and the stage replaced the
                strip without replacing them.
              */}
              {/*
                The meter, beside the reactions rather than among them.

                They are two different gestures and the difference matters: a
                reaction is a thing *you* did, with your name on a character in
                the crowd, once. This is a number the room is pushing up
                together, and nobody's name is on it.
              */}
              {listening && (
                <span className="pointer-events-auto flex items-center gap-3" data-stage-hype>
                  <HypeMeter total={hype.total} onTap={hype.tap} />

                  {/* The rule the reactions hang off, so the meter is not read
                      as one more glyph in a row of them - the lighter in the
                      reaction row is a fire too. */}
                  <span className="h-4 w-px bg-current opacity-20" aria-hidden />
                </span>
              )}

              {listening && (
                <span className="pointer-events-auto flex items-center gap-1" data-stage-reactions>
                  {REACTIONS.map(([reaction, glyph]) => (
                    <button
                      key={reaction}
                      type="button"
                      title={reaction}
                      aria-label={reaction}
                      data-stage-react={reaction}
                      disabled={react.isPending}
                      onClick={() => react.mutate(reaction)}
                      className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] text-base leading-none transition-colors hover:bg-white/10 disabled:opacity-50"
                    >
                      {glyph}
                    </button>
                  ))}
                </span>
              )}

              {/*
                The clock, beside the buttons rather than under the bar.

                It used to sit over the bar at the bottom, which put two lines
                of chrome across the front row's faces. There is nothing behind
                this corner but floor.
              */}
              <span className="font-mono text-[11px] text-white/45">
                <span data-live-position>{duration(position)}</span>
                {' / '}
                {track.duration_ms == null
                  ? '—'
                  : `-${duration(Math.max(0, track.duration_ms - position))}`}
              </span>

              <span className="pointer-events-auto flex items-center gap-2.5">
                {/* No `favorited` prop: the room payload goes to every listener
                    at once and cannot carry a per-viewer answer. */}
                <FavoriteButton
                  trackId={track.id}
                  size="button"
                  className="rounded-[var(--radius-pill)] border border-white/15 px-4 py-2.5 text-white"
                />
                <AddToPlaylist trackId={track.id} title={track.title} size="sm" />

                {zoomable && <ZoomButton zoomed={zoomed} onToggle={() => setFocused((on) => !on)} />}
              </span>
            </div>
          </div>
        </div>
      )}

      {/*
        The way back, when the row that holds the control is hidden.

        Bottom right on its own, which is where a video player keeps this and
        where it was a moment ago - the button should not move just because the
        thing around it did.
      */}
      {zoomable && (focused || track === null) && (
        <div className="absolute bottom-3 right-3 z-30">
          <ZoomButton zoomed={zoomed} onToggle={() => setFocused((on) => !on)} />
        </div>
      )}

      {/*
        The playhead, on the bottom edge of the venue.

        Flush and full-bleed, where a video player puts it - which is also the
        one line across this picture that crosses nobody's face. Inside the
        scrim it bisected the front row.

        Read-only, and deliberately not a scrub bar: everybody here is at the
        same second, which is what a broadcast is, and `useRoomSync` would undo
        a seek anyway.
      */}
      {track && !focused && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] bg-white/10">
          <div
            className="h-full bg-accent transition-[width] duration-300"
            style={{
              width: `${track.duration_ms ? Math.min(100, (position / track.duration_ms) * 100) : 0}%`,
            }}
          />
        </div>
      )}

    </div>
  );
}

/** Remembered per reader: somebody who wants the big video wants it every time. */
const REMEMBER = 'gv.stage.focused';

function remembered(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER) === '1';
  } catch {
    // Private windows and blocked site data both throw on access rather than
    // returning null, and a preference is not worth a broken page.
    return false;
  }
}

/**
 * Fill the frame with the screen, or put the room back.
 *
 * Deliberately not called fullscreen: the dock already has one of those and a
 * Twitch embed brings its own. This fills the frame, not the display.
 */
function ZoomButton({ zoomed, onToggle }: { zoomed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      data-stage-zoom
      aria-pressed={zoomed}
      title={zoomed ? 'Show the room' : 'Fill the frame'}
      aria-label={zoomed ? 'Show the room' : 'Fill the frame'}
      onClick={onToggle}
      className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-pill)] border border-white/15 text-white/80 transition-colors hover:border-white/30 hover:text-white"
    >
      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
        {zoomed ? (
          <path d="M6.5 2v4.5H2M9.5 14V9.5H14M2 9.5h4.5V14M14 6.5H9.5V2" strokeLinecap="round" />
        ) : (
          <path d="M2 6.5V2h4.5M14 9.5V14H9.5M2 9.5V14h4.5M14 6.5V2H9.5" strokeLinecap="round" />
        )}
      </svg>
    </button>
  );
}
